<template>
    <exportable-table
        v-model="list"
        rowKey="audience_origin_id"
        :headers="headers"
        :loading="loading"
        :filename="filename"
    >
        <template v-slot:tableTitle="{ totalCount }">{{ pluralized('audience origins', totalCount, true) }}</template>
        <template v-slot:rowTitle="{ item }">
            <div class="flex justify-between items-center gap-2 max-w-72">
                <div class="flex w-full gap-2 items-start">
                    <div class="min-w-16">
                        <vx-badge
                            class="cursor-pointer"
                            @click.prevent="copyText(item.audience_origin_id)"
                            size="xs"
                            color="slate"
                        >
                            {{ item.audience_origin_id }}
                        </vx-badge>
                    </div>
                    <div class="break-words line-clamp-4">
                        {{ item.audience_origin_name || '-' }}
                    </div>
                </div>

                <vx-popover hover arrow placement="top">
                    <font-awesome-icon
                        widthAuto
                        class="h-4 w-4 text-slate-500"
                        :icon="audienceOriginTypeIcon(item.audience_origin_type, 'regular')"
                    />

                    <template v-slot:content>
                        <span class="capitalize">{{ audienceOriginTypeLabel(item.audience_origin_type) }}</span>
                    </template>
                </vx-popover>
            </div>
        </template>
        <template v-slot:cell="{ value, field }">
            <template v-if="field === 'total_messages_sent'">
                <vx-popover
                    v-if="formatCount(value) !== ''"
                    arrow
                    hover
                    placement="top"
                    class="cursor-pointer"
                    @click="copyText(value)"
                >
                    {{ formatCount(value) }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(value)">
                            {{ formatNumber(value) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else-if="metricsWithGoals.includes(field)">
                <vx-popover
                    v-if="value !== null"
                    arrow
                    hover
                    placement="top"
                    @click="copyText(formatFractionToPercent(value, true))"
                    class="cursor-pointer"
                >
                    {{ formatPercentage(value, true) }}
                    <template v-slot:content>
                        <div class="text-center" @click="copyText(formatFractionToPercent(value, true))">
                            {{ formatFractionToPercent(value, true) }}
                            <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                        </div>
                    </template>
                </vx-popover>
                <span v-else>-</span>
            </template>
            <template v-else>
                {{ value }}
            </template>
        </template>
    </exportable-table>
</template>

<script setup>
import ExportableTable from '~/components/analytics/partial/exportable-table/ExportableTable.vue';
import { ref, watch } from 'vue';
import { outboundMessagesAnalyticsClient } from '~/services';
import { useListAllPaginated } from '~/composables/useListAllPaginated';
import {
    metricAbbreviation,
    metricLabel,
    metricsInputValidator,
    metricsWithGoals,
    audienceOriginTypeIcon,
    audienceOriginTypeLabel,
    isCampaign,
} from '~/utils/analytics';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { formatPercentage, formatCount, pluralized } from '~/components/filters';
import { VxBadge, VxPopover } from '@voxie/frontend-components';
import { useCopyText } from '~/composables/useCopyText';
import { formatFractionToPercent, formatNumber } from '~/utils/helpers';

const teamId = Spark.state.currentTeam.id;

const props = defineProps({
    orgUnitId: {
        type: Number,
    },
    groupId: {
        type: String,
    },
    fetchParams: {
        type: Object,
        required: true,
        validator: metricsInputValidator,
    },
    filename: {
        type: String,
        required: true,
    },
    onlyCampaign: {
        type: Boolean,
        default: false,
    },
});

const fetchAllParams = ref();
const { list, fetchAll, loading } = useListAllPaginated(fetchAllParams);

const { copyText } = useCopyText();

const headers = [
    { field: 'audience_origin_id', hidden: true },
    { field: 'audience_origin_name', label: 'Name', title: true, sortable: true },
    { field: 'audience_origin_type', hidden: true },
    {
        field: 'total_messages_sent',
        label: metricAbbreviation('total_messages_sent'),
        popoverContent: metricLabel('total_messages_sent'),
        sortable: true,
    },
    {
        field: 'unique_click_through_rate',
        label: metricAbbreviation('unique_click_through_rate'),
        popoverContent: metricLabel('unique_click_through_rate'),
        sortable: true,
    },
    {
        field: 'click_through_rate',
        label: metricAbbreviation('click_through_rate'),
        popoverContent: metricLabel('click_through_rate'),
        sortable: true,
    },
    {
        field: 'opt_out_rate',
        label: metricAbbreviation('opt_out_rate'),
        popoverContent: metricLabel('opt_out_rate'),
        sortable: true,
    },
    {
        field: 'message_reply_rate',
        label: metricAbbreviation('message_reply_rate'),
        popoverContent: metricLabel('message_reply_rate'),
        sortable: true,
    },
];

watch(
    () => props.fetchParams,
    () => {
        fetchAllParams.value = JSON.parse(JSON.stringify(props.fetchParams));
        const fetchParams = {
            ...fetchAllParams.value,
            filters: {
                ...fetchAllParams.value.filters,
                resources:
                    props.onlyCampaign && Array.isArray(fetchAllParams.value.filters.resources)
                        ? fetchAllParams.value.filters.resources.filter((resource) => isCampaign(resource))
                        : fetchAllParams.value.filters.resources,
            },
        };

        fetchAll(() =>
            outboundMessagesAnalyticsClient.getExportAudienceOriginMetrics(
                {
                    teamId,
                    orgUnitId: props.orgUnitId,
                    groupId: props.groupId,
                },
                fetchParams
            )
        );
    },
    {
        deep: true,
        immediate: true,
    }
);
</script>
